import { useEffect, useRef } from 'react';
import { parseToMinor } from '@debtbot/shared';

const NBSP = ' ';

/** "4500000" → "4 500 000" */
export function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Строка цифр (сумы, без тийинов) → minor units. */
export function digitsToMinor(digits: string, currency = 'UZS'): number {
  return digits ? (parseToMinor(digits, currency) ?? 0) : 0;
}

/**
 * Поле суммы: цифровая клавиатура, разделители тысяч на лету, автофокус.
 * Значение хранится как строка цифр в сумах (UZS без дробной части).
 */
export function AmountInput({
  digits,
  onChange,
  autoFocus = true,
  suffix = 'сум',
  warning,
}: {
  digits: string;
  onChange: (digits: string) => void;
  autoFocus?: boolean;
  suffix?: string;
  warning?: string | null;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // Сам <input> узкий (по ширине цифр), поэтому вся область — большая тап-зона: тап в любом
  // месте блока фокусирует поле и открывает цифровую клавиатуру. Прокрутка, закончившаяся
  // на блоке, фокус не ставит (отслеживаем смещение пальца).
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const focus = () => ref.current?.focus();

  return (
    <div
      className="flex min-h-[112px] cursor-text flex-col items-center justify-center gap-1 rounded-2xl px-4 py-4 active:bg-secondary-bg/60"
      onClick={focus}
      onTouchStart={(e) => {
        const t = e.touches[0];
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={(e) => {
        const start = touchStart.current;
        const t = e.changedTouches[0];
        if (!start || !t) return;
        const moved = Math.abs(t.clientX - start.x) > 10 || Math.abs(t.clientY - start.y) > 10;
        if (moved) return;
        // iOS открывает клавиатуру только из обработчика жеста пользователя
        e.preventDefault();
        focus();
      }}
      role="presentation"
    >
      <div className="flex items-baseline gap-2">
        <input
          ref={ref}
          data-testid="amount-input"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          value={groupDigits(digits)}
          onChange={(e) =>
            onChange(
              e.target.value
                .replace(/\D/g, '')
                .replace(/^0+(?=\d)/, '')
                .slice(0, 13),
            )
          }
          className="bg-transparent text-center text-4xl font-semibold tabular-nums outline-none placeholder:text-hint/50"
          style={{ width: `${Math.max(1, groupDigits(digits).length)}ch`, minWidth: '1ch' }}
        />
        <span className="text-xl text-hint">{suffix}</span>
      </div>
      {warning && <div className="text-sm text-warning">{warning}</div>}
    </div>
  );
}
